import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ImagePlus, ChevronUp, ChevronDown } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { STORAGE_BASE } from '../config';
import { AiImageButton } from './AiImage';
import { ICONS, ICON_NAMES } from '../sections/WidgetIcons';

export const uid = () => Math.random().toString(36).slice(2, 10);
export const resolveSrc = p => (!p ? '' : (/^https?:\/\//.test(p) ? p : `${STORAGE_BASE}/${String(p).replace(/^\/+/, '')}`));

export function useIsMobile() {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

export const fieldInput = { width: '100%', padding: '10px 14px', borderRadius: 12, border: '1.5px solid var(--input)', fontSize: '0.85rem', outline: 'none', background: 'var(--field)', color: 'var(--foreground)', direction: 'rtl', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.2s, box-shadow 0.2s' };

export const fieldCls = 'w-full rounded-xl border border-input bg-field px-3.5 py-2.5 text-[13px] text-foreground outline-none transition placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/20';
export const fieldLabelCls = 'mb-1.5 block text-right text-xs font-semibold text-muted-foreground';
export const fieldLabel = { display: 'block', marginBottom: 6, fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted-foreground)', textAlign: 'right' };
export const iconBtn = { width: 32, height: 32, borderRadius: 8, border: '1px solid var(--input)', background: 'var(--field)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted-foreground)', flexShrink: 0 };
export const focusRing = e => { e.target.style.borderColor = 'var(--ring)'; e.target.style.boxShadow = '0 0 0 3px color-mix(in srgb, var(--ring) 18%, transparent)'; };
export const blurRing = e => { e.target.style.borderColor = 'var(--input)'; e.target.style.boxShadow = 'none'; };

export const SHADOWS = {
  sm: '0 1px 2px rgba(0,0,0,0.06)',
  md: '0 4px 12px rgba(0,0,0,0.08)',
  lg: '0 8px 30px rgba(28,25,23,0.10)',
  xl: '0 16px 48px rgba(28,25,23,0.14)',
};
export const radiusCss = s => {
  const tl = s.radiusTL ?? s.radius ?? 0, tr = s.radiusTR ?? s.radius ?? 0;
  const br = s.radiusBR ?? s.radius ?? 0, bl = s.radiusBL ?? s.radius ?? 0;
  return (tl || tr || br || bl) ? `${tl}px ${tr}px ${br}px ${bl}px` : undefined;
};

export const FText = ({ label, value, onChange, placeholder }) => (
  <div><label className={fieldLabelCls}>{label}</label>
    <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={fieldCls} /></div>
);
export const FArea = ({ label, value, onChange, placeholder, rows }) => (
  <div><label className={fieldLabelCls}>{label}</label>
    <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows || 4} className={fieldCls} style={{resize: 'vertical', lineHeight: 1.9}} /></div>
);
export const FNumber = ({ label, value, onChange, min, max }) => (
  <div><label className={fieldLabelCls}>{label}</label>
    <input type="number" value={value ?? ''} onChange={e => onChange(Number(e.target.value))} min={min} max={max} className={fieldCls} /></div>
);
export const FSelect = ({ label, value, onChange, options }) => (
  <div><label className={fieldLabelCls}>{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)} className={fieldCls} style={{cursor: 'pointer'}}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select></div>
);
export const FColor = ({ label, value, onChange }) => (
  <div><label className={fieldLabelCls}>{label}</label>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(value || '') ? value : '#18181B'} onChange={e => onChange(e.target.value)}
        style={{ width: 44, height: 38, padding: 2, borderRadius: 10, border: '1.5px solid var(--input)', background: 'var(--field)', cursor: 'pointer', flexShrink: 0 }} />
      <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="#18181B یا transparent" className={fieldCls} dir="ltr" />
    </div></div>
);
export const FCheck = ({ label, checked, onChange }) => (
  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: 'var(--foreground)' }}>
    <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} style={{ accentColor: 'var(--brand)', width: 16, height: 16 }} /> {label}
  </label>
);

export function FImage({ label, value, onChange, accept, kind, suggested }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const prepare = file => new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/') || file.size <= 1.5 * 1024 * 1024) return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      const keepPng = file.type === 'image/png';
      canvas.toBlob(
        b => (b ? resolve(new File([b], file.name.replace(/\.\w+$/, keepPng ? '.png' : '.jpg'), { type: keepPng ? 'image/png' : 'image/jpeg' })) : reject(new Error('compress'))),
        keepPng ? 'image/png' : 'image/jpeg',
        0.82,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
    img.src = url;
  });
  const upload = async file => {
    if (!file) return;
    setUploading(true);
    try {
      const ready = await prepare(file);
      const fd = new FormData();
      fd.append('file', ready);
      const r = await axios.post('/media/upload', fd);
      onChange(r.data.path);
      toast.success('آپلود شد.', { autoClose: 5000 });
    } catch (err) {
      toast.error('خطا در آپلود: ' + (err.response?.data?.message || err.message), { autoClose: 5000 });
    }
    setUploading(false);
  };
  return (
    <div>
      <label className={fieldLabelCls}>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="آدرس یا آپلود" className={fieldCls} style={{direction: 'ltr', textAlign: 'left'}} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--brand)', color: 'var(--brand-foreground)', border: 'none', padding: '0 16px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 700, cursor: uploading ? 'default' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', opacity: uploading ? 0.6 : 1 }}>
          <ImagePlus size={15} /> {uploading ? '...' : 'آپلود'}
        </button>
        <AiImageButton onPick={onChange} resolveSrc={resolveSrc} suggested={suggested} />
        <input ref={fileRef} type="file" accept={accept || 'image/*'} style={{ display: 'none' }} onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} />
      </div>
      {value && (
        <div style={{ marginTop: 8, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', maxWidth: 280 }}>
          {kind === 'video' ? (
            <video src={resolveSrc(value)} controls style={{ width: '100%', display: 'block', maxHeight: 160, background: '#000' }} />
          ) : (
            <img src={resolveSrc(value)} alt="" style={{ width: '100%', display: 'block', maxHeight: 160, objectFit: 'cover' }} />
          )}
        </div>
      )}
    </div>
  );
}

export function FIconPicker({ label, value, onChange }) {
  const [q, setQ] = useState('');
  const list = ICON_NAMES.filter(n => n.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 72);
  const Sel = ICONS[value] || null;
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <label className={fieldLabelCls}>{label}</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <span style={{ width: 42, height: 42, borderRadius: 12, border: '1.5px solid var(--input)', background: 'var(--brand)', color: 'var(--brand-foreground)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {Sel ? <Sel size={20} /> : '?'}
        </span>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="جستجوی آیکون (نام انگلیسی)..." className={fieldCls} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxHeight: 190, overflowY: 'auto', padding: 8, border: '1.5px solid var(--input)', borderRadius: 12, background: 'var(--field)' }}>
        {list.map(n => {
          const C = ICONS[n];
          const active = value === n;
          return (
            <button key={n} type="button" title={n} onClick={() => onChange(n)}
              style={{ width: 38, height: 38, borderRadius: 10, border: active ? '2px solid var(--brand)' : '1px solid var(--input)', background: active ? 'var(--brand)' : 'var(--card)', color: active ? 'var(--brand-foreground)' : 'var(--muted-foreground)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <C size={18} />
            </button>
          );
        })}
        {list.length === 0 && <span style={{ fontSize: '0.8rem', color: 'var(--muted-foreground)', padding: 8 }}>آیکونی یافت نشد.</span>}
      </div>
    </div>
  );
}

export function CategorySelect({ value, onChange }) {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    axios.get('/categories')
      .then(r => setCats(Array.isArray(r.data) ? r.data : (r.data?.data || [])))
      .catch(() => {});
  }, []);
  return (
    <FSelect label="دسته‌بندی" value={value} onChange={onChange}
      options={[{ value: '', label: 'همه محصولات' }, ...cats.map(c => ({ value: String(c.id), label: c.name }))]} />
  );
}

export const MENU_ITEM_TYPES = [
  { value: 'link', label: 'لینک دلخواه' },
  { value: 'page', label: 'برگه' },
  { value: 'product_cat', label: 'دسته محصول (تکی)' },
  { value: 'product_cats', label: 'همه دسته‌های محصول (خودکار)' },
  { value: 'post_cat', label: 'دسته وبلاگ (تکی)' },
  { value: 'post_cats', label: 'همه دسته‌های وبلاگ (خودکار)' },
];

export function LinksEditor({ items = [], onChange, withChildren, withTypes, sources = {} }) {
  const set = list => onChange(list);
  const upd = (i, patch) => set(items.map((it, x) => x === i ? { ...it, ...patch } : it));
  const typeOf = it => it.menuType || 'link';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((it, i) => (
        <div key={i} style={{ border: '1px solid #E4E4E7', borderRadius: 12, padding: 12, background: '#FAFAFA', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <strong style={{ fontSize: '0.82rem', color: '#18181B' }}>لینک {i + 1}</strong>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => { if (i === 0) return; const n = [...items]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; set(n); }}
                style={{ ...iconBtn, width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><ChevronUp size={14} /></button>
              <button type="button" onClick={() => { if (i === items.length - 1) return; const n = [...items]; [n[i], n[i + 1]] = [n[i + 1], n[i]]; set(n); }}
                style={{ ...iconBtn, width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><ChevronDown size={14} /></button>
              <button type="button" onClick={() => set(items.filter((_, x) => x !== i))}
                style={{ background: '#FEF2F2', color: '#991B1B', border: 'none', padding: '6px 12px', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>حذف</button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            <FText label="عنوان" value={it.label} onChange={v => upd(i, { label: v })} placeholder="عنوان لینک" />
            {withTypes && (
              <FSelect label="نوع آیتم" value={typeOf(it)} onChange={v => upd(i, { menuType: v })}
                options={MENU_ITEM_TYPES} />
            )}
            {(!withTypes || typeOf(it) === 'link') && (
              <FText label="آدرس" value={it.url} onChange={v => upd(i, { url: v })} placeholder="/products یا https://..." />
            )}
            {withTypes && typeOf(it) === 'page' && (
              <FSelect label="برگه" value={it.ref || ''} onChange={v => upd(i, { ref: v })}
                options={[{ value: '', label: 'انتخاب کنید...' }, ...(sources.pages || []).map(p => ({ value: p.slug, label: `${p.title} (/${p.slug})` }))]} />
            )}
            {withTypes && typeOf(it) === 'product_cat' && (
              <FSelect label="دسته محصول" value={it.ref || ''} onChange={v => upd(i, { ref: v })}
                options={[{ value: '', label: 'انتخاب کنید...' }, ...(sources.categories || []).map(c => ({ value: String(c.id), label: c.name }))]} />
            )}
            {withTypes && typeOf(it) === 'post_cat' && (
              <FSelect label="دسته وبلاگ" value={it.ref || ''} onChange={v => upd(i, { ref: v })}
                options={[{ value: '', label: 'انتخاب کنید...' }, ...(sources.postCats || []).map(c => ({ value: c.slug, label: c.name }))]} />
            )}
          </div>
          {withTypes && (typeOf(it) === 'product_cats' || typeOf(it) === 'post_cats') && (
            <div style={{ fontSize: '0.74rem', color: '#71717A', background: '#fff', border: '1px dashed #D4D4D8', borderRadius: 8, padding: '8px 12px' }}>
              زیرمنوها به‌صورت خودکار از {typeOf(it) === 'product_cats' ? 'دسته‌بندی‌های محصولات' : 'دسته‌بندی‌های وبلاگ'} لود می‌شوند.
            </div>
          )}
          {withChildren && (
            <div style={{ borderTop: '1px dashed #E4E4E7', paddingTop: 10 }}>
              <div style={{ ...fieldLabel, marginBottom: 8 }}>زیرمنو (اختیاری — برای آیتم‌های خودکار نادیده گرفته می‌شود)</div>
              <LinksEditor items={it.children || []} onChange={v => upd(i, { children: v })} withTypes={withTypes} sources={sources} />
            </div>
          )}
        </div>
      ))}
      <button type="button" onClick={() => set([...items, { label: '', url: '' }])}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#F4F4F5', color: '#18181B', border: '1px dashed #D4D4D8', padding: '10px', borderRadius: 12, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
        + افزودن لینک
      </button>
    </div>
  );
}

export function StyleFields({ s, update }) {
  return (<>
    <FColor label="رنگ متن" value={s.color} onChange={v => update('color', v)} />
    <FColor label="پس‌زمینه" value={s.bg} onChange={v => update('bg', v)} />
    <FSelect label="تراز" value={s.align || 'right'} onChange={v => update('align', v)}
      options={[{ value: 'right', label: 'راست' }, { value: 'center', label: 'وسط' }, { value: 'left', label: 'چپ' }]} />
    <FNumber label="اندازه فونت (px)" value={s.fontSize} onChange={v => update('fontSize', v)} min={10} max={72} />
  </>);
}

export function BoxStyleFields({ v = {}, onChange, marginKey, marginLabel }) {
  const set = (k, val) => onChange({ ...v, [k]: val });
  const corner = (key, label) => (
    <div><label className={fieldLabelCls}>{label}</label>
      <input type="number" value={v[key] ?? v.radius ?? ''} onChange={e => set(key, Number(e.target.value))} min={0} max={80}
        className={fieldCls} /></div>
  );
  return (<>
    <div style={{ gridColumn: '1 / -1' }}>
      <div className={fieldLabelCls}>گردی هر گوشه (px)</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {corner('radiusTR', 'بالا راست')}
        {corner('radiusTL', 'بالا چپ')}
        {corner('radiusBR', 'پایین راست')}
        {corner('radiusBL', 'پایین چپ')}
      </div>
    </div>
    <FSelect label="سایه" value={v.shadow || ''} onChange={val => set('shadow', val)}
      options={[{ value: '', label: 'بدون سایه' }, { value: 'sm', label: 'کم' }, { value: 'md', label: 'متوسط' }, { value: 'lg', label: 'زیاد' }, { value: 'xl', label: 'خیلی زیاد' }]} />
    {marginKey && <FNumber label={marginLabel || 'فاصله بیرونی (px)'} value={v[marginKey]} onChange={val => set(marginKey, val)} min={0} max={80} />}
  </>);
}

export function SidesFields({ v = {}, onChange, prefix, title }) {
  const set = (k, val) => onChange({ ...v, [`${prefix}${k}`]: val });
  const side = (k, label) => (
    <div><label className={fieldLabelCls}>{label}</label>
      <input type="number" value={v[`${prefix}${k}`] ?? ''} onChange={e => set(k, Number(e.target.value))} min={0} max={120}
        className={fieldCls} /></div>
  );
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <div className={fieldLabelCls}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {side('T', 'بالا')}
        {side('R', 'راست')}
        {side('B', 'پایین')}
        {side('L', 'چپ')}
      </div>
    </div>
  );
}

export function FrameFields({ s, update }) {
  const f = s.frame || {};
  const uf = obj => update('frame', obj);
  return (<>
    <FColor label="پس‌زمینه قاب ویجت" value={f.bg} onChange={v => uf({ ...f, bg: v })} />
    <FNumber label="فاصله داخلی قاب (px)" value={f.padding} onChange={v => uf({ ...f, padding: v })} min={0} max={60} />
    <BoxStyleFields v={f} onChange={uf} />
    <SidesFields v={f} onChange={uf} prefix="margin" title="فاصله بیرونی قاب از چهار طرف (px)" />
  </>);
}

export function TitleStyleFields({ s, update }) {
  return (<>
    <FColor label="رنگ عنوان" value={s.titleColor} onChange={v => update('titleColor', v)} />
    <FNumber label="اندازه عنوان (px)" value={s.titleSize} onChange={v => update('titleSize', v)} min={14} max={56} />
  </>);
}
